import { useTranslation } from "react-i18next";
import { EntityList } from "@/components/common/EntityList";
import { ListMember } from "@/types/list";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Trash2, MapPin } from "lucide-react";
import { Link } from "react-router-dom";

interface ListMembersListProps {
  members: ListMember[];
  onMembersChange?: () => void;
  onRemoveMember?: (memberId: string) => void;
}

export const ListMembersList = ({ members, onMembersChange, onRemoveMember }: ListMembersListProps) => {
  const { t } = useTranslation();

  const columns = [
    {
      key: "name",
      header: t("lists.members.list.name", "Name"),
      cell: (member: ListMember) => (
        <div>
          <Link 
            to={`/candidates/${member.candidate.id}`} 
            className="font-medium text-gray-900 hover:text-blue-500 hover:underline transition-colors"
          >
            {member.candidate.name}
          </Link>
          {member.candidate.function && (
            <div className="text-sm text-gray-500">{member.candidate.function}</div>
          )}
        </div>
      ),
    },

    {
      key: "location",
      header: t("lists.members.list.location", "Location"),
      cell: (member: ListMember) => (
        member.candidate.location ? (
          <div className="flex items-center text-sm text-gray-600">
            <MapPin className="h-3 w-3 mr-1" />
            <span className="truncate">{member.candidate.location}</span>
          </div>
        ) : (
          <span className="text-sm text-gray-400">-</span>
        )
      ),
    },
    {
      key: "skills",
      header: t("lists.members.list.skills", "Skills"),
      cell: (member: ListMember) => (
        <div className="flex flex-wrap gap-1">
          {member.candidate.skills && member.candidate.skills.length > 0 ? (
            <>
              {member.candidate.skills.slice(0, 2).map((skill, index) => (
                <Badge key={index} variant="outline" className="text-xs">
                  {skill}
                </Badge>
              ))}
              {member.candidate.skills.length > 2 && (
                <Badge variant="outline" className="text-xs">
                  +{member.candidate.skills.length - 2}
                </Badge>
              )}
            </>
          ) : (
            <span className="text-sm text-gray-400">-</span>
          )}
        </div>
      ),
    },
    {
      key: "addedAt",
      header: t("lists.members.list.addedAt", "Added"),
      cell: (member: ListMember) => (
        <div className="text-sm text-gray-500">
          {format(new Date(member.added_at), "MMM dd, yyyy")}
        </div>
      ),
    },
    {
      key: "remove",
      header: t("lists.members.list.remove", "Remove"),
      cell: (member: ListMember) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onRemoveMember?.(member.candidate_id)}
          className="text-red-600 hover:text-red-800 hover:bg-red-50"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      ),
    },

  ];

  if (!members || members.length === 0) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          {t("lists.members.empty.title", "No candidates found")}
        </h3>
        <p className="text-gray-600">
          {t("lists.members.empty.description", "Add candidates to this list to get started")}
        </p>
      </div>
    );
  }

  return (
    <EntityList
      data={members}
      columns={columns}
      emptyMessage={t("lists.members.empty", "No candidates in this list")}
      showActionsColumn={false}
    />
  );
};
